import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useWindowSizeClass } from '../hooks/useWindowSizeClass';
import { useTheme } from '../hooks/useTheme';

import '@material/web/labs/navigationbar/navigation-bar.js';
import '@material/web/labs/navigationtab/navigation-tab.js';
import '@material/web/iconbutton/icon-button.js';
import '@material/web/iconbutton/filled-tonal-icon-button.js';
import '@material/web/icon/icon.js';
import './NavigationSuite.css';

const TAB_PATHS = ['/', '/search', '/mylist', '/profile'];

export default function NavigationSuite() {
    const navigate = useNavigate();
    const location = useLocation();
    const { isCompact, isMedium } = useWindowSizeClass();
    const { contrastLevel, setContrastLevel, isDark, setIsDark } = useTheme();

    const [isScrolled, setIsScrolled] = useState(false);
    const [avatarSrc, setAvatarSrc] = useState('https://upload.wikimedia.org/wikipedia/commons/0/0b/Netflix-avatar.png');
    const navBarRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 30);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const loadAvatar = () => {
            const stored = localStorage.getItem('customAvatar');
            if (stored) setAvatarSrc(stored);
        };
        loadAvatar();
        window.addEventListener('avatar-updated', loadAvatar);
        return () => window.removeEventListener('avatar-updated', loadAvatar);
    }, []);

    useEffect(() => {
        if (isMedium) {
            document.body.classList.add('has-rail');
        } else {
            document.body.classList.remove('has-rail');
        }
        return () => document.body.classList.remove('has-rail');
    }, [isMedium]);

    const getActiveIndex = (pathname) => {
        if (pathname === '/' || pathname === '/home') return 0;
        if (pathname.startsWith('/search')) return 1;
        if (pathname.startsWith('/mylist')) return 2;
        if (pathname.startsWith('/profile')) return 3;
        // Dettagli e altre pagine non sono una scheda: nessuna voce evidenziata
        return -1;
    };

    const activeIndex = getActiveIndex(location.pathname);

    useEffect(() => {
        const navBar = navBarRef.current;
        if (!navBar) return;
        let cancelled = false;

        // md-navigation-bar lancia un'eccezione se activeIndex non e' una scheda
        // ("activeIndex is out of bounds"): per Dettagli e le altre pagine fuori dalle
        // schede si lascia l'indice com'e' e si spengono solo le schede.
        if (activeIndex >= 0) navBar.activeIndex = activeIndex;
        const syncTabs = () => {
            if (cancelled) return;
            navBar.querySelectorAll('md-navigation-tab').forEach((tab, i) => {
                tab.active = i === activeIndex;
            });
        };
        // la barra registra le schede e ne imposta lo stato in modo asincrono:
        // si riallinea dopo i suoi aggiornamenti
        Promise.resolve(navBar.updateComplete).then(syncTabs);
        const timer = setTimeout(syncTabs, 0);

        // Si naviga dal tocco sulla scheda, non da "navigation-bar-activated": quello
        // non parte se l'indice non cambia (es. da Dettagli a Home quando l'indice era gia' 0).
        const handleTab = (e) => {
            const tabs = [...navBar.querySelectorAll('md-navigation-tab')];
            const path = TAB_PATHS[tabs.indexOf(e.detail?.state)];
            if (path) navigate(path);
        };

        navBar.addEventListener('navigation-tab-interaction', handleTab);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            navBar.removeEventListener('navigation-tab-interaction', handleTab);
        };
    }, [activeIndex, navigate]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Failed to log out', error);
        }
    };

    const toggleContrast = () => {
        if (contrastLevel === 'standard') setContrastLevel('medium');
        else if (contrastLevel === 'medium') setContrastLevel('high');
        else setContrastLevel('standard');
    };

    const contrastLabel = {
        standard: 'Contrasto standard',
        medium: 'Contrasto medio',
        high: 'Contrasto elevato'
    }[contrastLevel] || 'Contrasto';

    // 1. COMPACT LAYOUT (< 600px): Top Minimal Bar + Bottom Navigation Bar
    if (isCompact) {
        return (
            <>
                <header className="m3-compact-top-bar">
                    <NavLink to="/" className="m3-top-app-bar__logo">
                        Notflix
                    </NavLink>
                    <div className="m3-compact-top-bar__actions" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <md-icon-button
                            onClick={() => setIsDark(!isDark)}
                            title={isDark ? "Passa a tema chiaro" : "Passa a tema scuro"}
                            aria-label={isDark ? "Passa a tema chiaro" : "Passa a tema scuro"}
                        >
                            <md-icon>
                                <span className="material-symbols-rounded">{isDark ? 'light_mode' : 'dark_mode'}</span>
                            </md-icon>
                        </md-icon-button>

                        {/* sul telefono il contrasto sta solo nel Profilo: la barra resta leggera */}
                        <div
                            className="m3-avatar-btn"
                            style={{ width: '40px', height: '40px' }}
                            onClick={() => navigate('/profile')}
                            title="Profilo"
                        >
                            <img src={avatarSrc} alt="Profilo" />
                        </div>
                    </div>
                </header>

                <nav className="m3-bottom-nav-container" aria-label="Navigazione principale mobile">
                    <md-navigation-bar ref={navBarRef}>
                        <md-navigation-tab label="Home" active={activeIndex === 0}>
                            <md-icon slot="active-icon">
                                <span className="material-symbols-rounded filled">home</span>
                            </md-icon>
                            <md-icon slot="inactive-icon">
                                <span className="material-symbols-rounded">home</span>
                            </md-icon>
                        </md-navigation-tab>

                        <md-navigation-tab label="Cerca" active={activeIndex === 1}>
                            <md-icon slot="active-icon">
                                <span className="material-symbols-rounded filled">search</span>
                            </md-icon>
                            <md-icon slot="inactive-icon">
                                <span className="material-symbols-rounded">search</span>
                            </md-icon>
                        </md-navigation-tab>

                        <md-navigation-tab label="La mia lista" active={activeIndex === 2}>
                            <md-icon slot="active-icon">
                                <span className="material-symbols-rounded filled">video_library</span>
                            </md-icon>
                            <md-icon slot="inactive-icon">
                                <span className="material-symbols-rounded">video_library</span>
                            </md-icon>
                        </md-navigation-tab>

                        <md-navigation-tab label="Profilo" active={activeIndex === 3}>
                            <md-icon slot="active-icon">
                                <span className="material-symbols-rounded filled">account_circle</span>
                            </md-icon>
                            <md-icon slot="inactive-icon">
                                <span className="material-symbols-rounded">account_circle</span>
                            </md-icon>
                        </md-navigation-tab>
                    </md-navigation-bar>
                </nav>
            </>
        );
    }

    // 2. MEDIUM LAYOUT (600px - 839px): Side Navigation Rail
    if (isMedium) {
        return (
            <aside className="m3-navigation-rail" aria-label="Navigazione laterale tablet">
                <div className="m3-nav-rail__header">
                    <NavLink to="/" className="m3-top-app-bar__logo" title="Notflix">
                        <span style={{ fontSize: '1.8rem', fontWeight: 900 }}>N</span>
                    </NavLink>
                </div>

                <div className="m3-nav-rail__destinations">
                    <NavLink
                        to="/"
                        className={({ isActive }) => isActive ? 'm3-nav-rail__tab active' : 'm3-nav-rail__tab'}
                        title="Home"
                    >
                        <span className="material-symbols-rounded">home</span>
                        <span>Home</span>
                    </NavLink>

                    <NavLink
                        to="/search"
                        className={({ isActive }) => isActive ? 'm3-nav-rail__tab active' : 'm3-nav-rail__tab'}
                        title="Cerca"
                    >
                        <span className="material-symbols-rounded">search</span>
                        <span>Cerca</span>
                    </NavLink>

                    <NavLink
                        to="/mylist"
                        className={({ isActive }) => isActive ? 'm3-nav-rail__tab active' : 'm3-nav-rail__tab'}
                        title="La mia lista"
                    >
                        <span className="material-symbols-rounded">video_library</span>
                        <span>Lista</span>
                    </NavLink>
                </div>

                <div className="m3-nav-rail__footer">
                    <md-icon-button onClick={() => setIsDark(!isDark)} title={isDark ? "Passa a tema chiaro" : "Passa a tema scuro"} aria-label={isDark ? "Passa a tema chiaro" : "Passa a tema scuro"}>
                        <md-icon>
                            <span className="material-symbols-rounded">{isDark ? 'light_mode' : 'dark_mode'}</span>
                        </md-icon>
                    </md-icon-button>

                    <md-icon-button onClick={toggleContrast} title={contrastLabel} aria-label={contrastLabel}>
                        <md-icon>
                            <span className="material-symbols-rounded">contrast</span>
                        </md-icon>
                    </md-icon-button>

                    <div className="m3-avatar-btn" onClick={() => navigate('/profile')} title="Profilo">
                        <img src={avatarSrc} alt="Profilo" />
                    </div>

                    <md-icon-button onClick={handleLogout} title="Esci dall'account" aria-label="Logout">
                        <md-icon>
                            <span className="material-symbols-rounded">logout</span>
                        </md-icon>
                    </md-icon-button>
                </div>
            </aside>
        );
    }

    // 3. EXPANDED LAYOUT (>= 840px): Full Top App Bar with Tonal Elevation
    return (
        <header className={`m3-top-app-bar ${isScrolled ? 'scrolled' : ''}`} aria-label="Barra superiore">
            <div className="m3-top-app-bar__leading">
                <NavLink to="/" className="m3-top-app-bar__logo">
                    Notflix
                </NavLink>

                <nav className="m3-top-app-bar__nav-links">
                    <NavLink
                        to="/"
                        className={({ isActive }) => isActive ? 'm3-nav-link active' : 'm3-nav-link'}
                    >
                        <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>home</span>
                        Home
                    </NavLink>
                    <NavLink
                        to="/search"
                        className={({ isActive }) => isActive ? 'm3-nav-link active' : 'm3-nav-link'}
                    >
                        <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>search</span>
                        Cerca
                    </NavLink>
                    <NavLink
                        to="/mylist"
                        className={({ isActive }) => isActive ? 'm3-nav-link active' : 'm3-nav-link'}
                    >
                        <span className="material-symbols-rounded" style={{ fontSize: '18px' }}>video_library</span>
                        La mia lista
                    </NavLink>
                </nav>
            </div>

            <div className="m3-top-app-bar__trailing">
                <md-icon-button
                    onClick={() => setIsDark(!isDark)}
                    title={isDark ? "Passa a tema chiaro" : "Passa a tema scuro"}
                    aria-label={isDark ? "Passa a tema chiaro" : "Passa a tema scuro"}
                >
                    <md-icon>
                        <span className="material-symbols-rounded">{isDark ? 'light_mode' : 'dark_mode'}</span>
                    </md-icon>
                </md-icon-button>

                <md-icon-button
                    onClick={toggleContrast}
                    title={contrastLabel}
                    aria-label={contrastLabel}
                >
                    <md-icon>
                        <span className="material-symbols-rounded">contrast</span>
                    </md-icon>
                </md-icon-button>

                <div
                    className="m3-avatar-btn"
                    onClick={() => navigate('/profile')}
                    title="Vai al tuo Profilo"
                >
                    <img src={avatarSrc} alt="Profilo" />
                </div>

                <button type="button" className="m3-logout-btn" onClick={handleLogout} aria-label="Disconnetti account">
                    <span className="material-symbols-rounded">logout</span>
                    Esci
                </button>
            </div>
        </header>
    );
}
